<template>
	<button
		type="button"
		class="_mute-button"
		:aria-label="muted ? 'Unmute video' : 'Mute video'"
		:aria-pressed="!muted"
		@click="onClick"
		@mouseenter="onMouseEnter"
		@mouseleave="onMouseLeave"
	>
		<span class="background" />
		<span ref="label" class="label -p-small">
			<span ref="labelSlot" class="label-slot">
				<span ref="labelTextElement" class="label-text">{{ labelText }}</span>
			</span>
		</span>
		<svg
			class="icon"
			width="24"
			height="24"
			viewBox="0 0 24 24"
			xmlns="http://www.w3.org/2000/svg"
			aria-hidden="true"
		>
			<g :class="{ '-active': muted }">
				<path d="M4 8.5L3 10V13.5L4 15H7L11 21L12 20V4L10.5 3.5L7 9L4 8.5Z" />
				<path
					d="M18.7524 11.8389L21.9344 9.36406L20.5202 7.94984L19.4994 7.94984L17.3382 10.4247L14.1562 14.3138L14.8633 16.4351L18.7524 11.8389Z"
				/>
				<path
					d="M21.4853 13.3637L17.9497 11.2424L15 7.99979L14.4142 7.70688L13.7071 9.8282L16.5355 12.6566L20 15.4851L20.7782 15.4851L21.4853 13.3637Z"
				/>
			</g>
			<g :class="{ '-active': !muted }">
				<path d="M4 8.5L3 10V13.5L4 15H7L11 21L12 20V4L10.5 3.5L7 9L4 8.5Z" />
				<path
					d="M16.5 4.5L16 6L18 8.5L19 11.5L18.5 15.5L16 20L18 20.5L20.5 16.5L21 10.5L20 7L16.5 4.5Z"
				/>
				<path d="M16 15.5L14.5 16.5L15 13L14.5 9H16L17 13L16 15.5Z" />
			</g>
		</svg>
	</button>
</template>

<script setup>
import { gsap } from 'gsap'
import { useScreen } from 'vue-screen'

const props = defineProps({
	modelValue: { type: Boolean, default: true },
})

const emit = defineEmits(['update:modelValue', 'mute', 'unmute', 'toggle'])

const screen = useScreen()
const label = ref(null)
const labelSlot = ref(null)
const labelTextElement = ref(null)
const labelText = ref(props.modelValue ? 'Unmute' : 'Mute')
const labelExpanded = ref(false)
const muted = computed(() => props.modelValue)

const onClick = () => {
	const nextValue = !props.modelValue

	emit('update:modelValue', nextValue)
	emit(nextValue ? 'mute' : 'unmute')
	emit('toggle', nextValue)
}

watch(
	() => props.modelValue,
	async (value, oldValue) => {
		if (value === oldValue) return

		const nextText = value ? 'Unmute' : 'Mute'

		if (screen.touch || !labelTextElement.value) {
			labelText.value = nextText
			return
		}

		const previousWidth = labelSlot.value?.offsetWidth || 0

		if (labelExpanded.value && label.value) {
			gsap.set(label.value, { width: previousWidth })
		}

		await new Promise((resolve) => {
			gsap.killTweensOf(labelTextElement.value)
			gsap.to(labelTextElement.value, {
				duration: 0.18,
				yPercent: -101,
				opacity: 0,
				ease: 'power2.in',
				onComplete: resolve,
			})
		})

		labelText.value = nextText
		await nextTick()

		const nextWidth = labelSlot.value?.offsetWidth || 0

		gsap.set(labelTextElement.value, { yPercent: 101, opacity: 0 })

		if (labelExpanded.value && label.value) {
			gsap.to(label.value, {
				duration: 0.24,
				width: nextWidth,
				ease: 'power2.inOut',
				onComplete: () => gsap.set(label.value, { width: 'auto' }),
			})
		}

		gsap.to(labelTextElement.value, {
			duration: 0.24,
			yPercent: 0,
			opacity: 1,
			ease: 'power2.out',
		})
	},
)

const onMouseEnter = () => {
	if (screen.touch || !label.value) return

	const labelWidth = labelSlot.value?.offsetWidth || 0

	gsap.killTweensOf(label.value)
	gsap.to(label.value, {
		duration: 0.3,
		width: labelWidth,
		ease: 'power2.inOut',
		onComplete: () => {
			labelExpanded.value = true
			gsap.set(label.value, { width: 'auto' })
		},
	})
}

const onMouseLeave = () => {
	if (screen.touch || !label.value) return

	gsap.killTweensOf(label.value)
	gsap.to(label.value, {
		duration: 0.3,
		width: 0,
		ease: 'power2.inOut',
		onComplete: () => {
			labelExpanded.value = false
		},
	})
}

onBeforeUnmount(() => {
	label.value && gsap.killTweensOf(label.value)
	labelTextElement.value && gsap.killTweensOf(labelTextElement.value)
})
</script>

<style lang="scss" scoped>
._mute-button {
	position: relative;
	display: flex;
	flex-direction: row-reverse;
	align-items: center;
	padding: 11px;

	color: $white;
	background: transparent;
	border: 0;
	border-radius: 60px;
	cursor: pointer;

	& > .background {
		position: absolute;
		inset: 0;
		background-color: rgba(#a6a6a6, 0.15);
		backdrop-filter: blur(20px);
		border-radius: inherit;
		transition: transform 0.3s ease('power2', 'out'), opacity 0.3s ease('power2', 'out');
	}

	& > .label {
		position: relative;
		z-index: 2;
		width: 0;
		overflow: hidden;
		pointer-events: none;
		white-space: nowrap;

		.label-slot {
			display: block;
			width: max-content;
			padding-left: 5px;
			padding-right: 11px;
			overflow: hidden;
		}

		.label-text {
			display: block;
			color: $white;
		}
	}

	& > .icon {
		position: relative;
		z-index: 2;
		order: -1;
		width: 24px;
		height: 24px;
		flex-shrink: 0;
		fill: currentColor;

		path {
			fill: $white;
		}
		g {
			visibility: hidden;
		}

		.-active {
			visibility: visible;
		}
	}

	@include hover-supported {
		&:hover {
			& > .background {
				transform: scale(1.08);
				opacity: 0.9;
			}

			& > .label {
				pointer-events: all;
			}
		}

		&:active {
			& > .background {
				transform: scale(1.02);
				opacity: 0.8;
			}
		}
	}
}
</style>
